import { zodResolver } from '@hookform/resolvers/zod'
import { Box, DialogContent, InputAdornment, SvgIcon, Typography } from '@mui/material'
import { differenceInMinutes } from 'date-fns'
import PropTypes from 'prop-types'
import { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import ClockSmallIcon from '@hapana/shared/web/styles/assets/svgs/clock-small.svg'
import PlaySmallIcon from '@hapana/shared/web/styles/assets/svgs/play-small.svg'
import InfoSmallIcon from '@hapana/shared/web/styles/assets/svgs/info-small.svg'
import PenSmallIcon from '@hapana/shared/web/styles/assets/svgs/pen-small.svg'
import TrashSmallIcon from '@hapana/shared/web/styles/assets/svgs/trash-small.svg'
import Avatar from '@hapana/shared/web/components/shared/avatar/avatar.component'
import {
  AVATAR_SIZE,
  AVATAR_TYPES,
  AVATAR_VARIANT,
} from '@hapana/shared/web/components/shared/avatar/avatar.constants'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_THEME,
  BUTTON_VARIANTS,
} from '@hapana/shared/web/components/shared/button/button.constants'
import { getFormattedImageName } from '@hapana/shared/web/components/shared/core-user/user-access/components/add-edit-user-access.constants'
import DialogLayout from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import Dropdown from '@hapana/shared/web/components/shared/dropdown/dropdown.component'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import ModalGroup from '@hapana/shared/web/components/shared/modal-group/modal-group.component'
import ModalUploadPhoto from '@hapana/shared/web/components/shared/modal-upload-photo/modal-upload-photo.component'
import { RADIO_TYPES } from '@hapana/shared/web/components/shared/radio-button/radio-button.constants'
import RadioGroup from '@hapana/shared/web/components/shared/radio-group/radio-group.component'
import Switch from '@hapana/shared/web/components/shared/switch/switch.component'
import { SWITCH_TYPES } from '@hapana/shared/web/components/shared/switch/switch.constants'
import TextArea from '@hapana/shared/web/components/shared/text-area/text-area.component'
import TextField from '@hapana/shared/web/components/shared/text-field/text-field.component'
import { imageToUrl } from '@hapana/shared/utils/file.utils'
import { STATUS_I18N_KEY_MAP } from '_components/check-in-widget/most-recent-card/equipments-available/assign-to-equipment-dialog/assign-to-equipment-dialog.constants'
import { EQUIPMENT_CATEGORIES, EQUIPMENT_STATUSES } from '_constants/equipments'
import {
  useDeleteEquipment,
  useEquipmentImageUpload,
  useGetEquipment,
  useUpdateEquipment,
} from '_hooks/equipments.hooks'
import { useClientsByIds } from '_hooks/clients.hooks'
import { useSiteSessionTypes } from '_pages/scheduler-settings/session-types/session-types.hooks'
import useSiteStore from '_stores/site.store'

import { EQUIPMENT_QUALITY_CATEGORIES_OPTIONS } from '../manage-equipments.constants'
import {
  DEFAULT_VALUES,
  FIELD_NAMES,
  NON_UV_CATEGORY_OPTIONS,
  VALIDATION_SCHEMA,
} from '../add-equipment-dialog/add-equipment-dialog.constants'
import { EquipmentTestLogDialog } from '../equipment-detail-dialog/test-log/test-log.component'

import { ROUTES } from '../../../../../../app.constants'
import { styles } from './equipment-edit-dialog.styles'

const InfoPanel = ({ type, dataList, action }) => (
  <Box sx={[styles.infoPanel, type === 'time' ? styles.infoPanelTime : styles.infoPanelInfo]}>
    <Box sx={styles.infoPanelContent}>
      <SvgIcon
        component={type === 'time' ? ClockSmallIcon : InfoSmallIcon}
        sx={[
          styles.infoPanelIcon,
          type === 'time' ? styles.infoPanelIconTime : styles.infoPanelIconInfo,
        ]}
      />
      <Box sx={styles.infoPanelDataContainer}>
        {dataList.map((item) => (
          <Box key={item.label} sx={styles.infoPanelDataRow}>
            <Typography typography="header4" color="gray.80">
              {item.label}
            </Typography>
            <Typography typography="medium" color="gray.80">
              {item.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
    {action && (
      <Button
        label={action.label}
        action={action.action}
        href={action.href}
        variant={BUTTON_VARIANTS.TEXT}
      />
    )}
  </Box>
)

InfoPanel.propTypes = {
  type: PropTypes.string.isRequired,
  dataList: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    })
  ).isRequired,
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    action: PropTypes.func,
    href: PropTypes.string,
  }),
}

export const EquipmentEditDialog = ({ equipmentId }) => {
  const { t } = useTranslation('checkIn')
  const { popDialog, pushDialog, setDialogState, dialogState, filterDialogStack } = useDialog()
  const siteId = useSiteStore((state) => state.currentSite?.siteId)

  const dialogFilterParams = useMemo(
    () => ({ key: 'title', value: t('checkIn:equipments.addEquipment.editTitle') }),
    [t]
  )

  const { mutate: updateEquipment, isLoading: isUpdating } = useUpdateEquipment({
    onSuccess: () => {
      setDialogState({
        defaultTab: '3',
        formValues: null,
        image: null,
        imageExtension: null,
        croppedImage: null,
      })
      filterDialogStack('title', 'checkIn')
    },
  })
  const { mutate: deleteEquipment, isLoading: isDeleting } = useDeleteEquipment({
    onSuccess: () => {
      setDialogState({
        defaultTab: '3',
        formValues: null,
        image: null,
        imageExtension: null,
        croppedImage: null,
      })
    },
  })

  const { data: equipmentData, isLoading } = useGetEquipment(siteId, equipmentId, {
    staleTime: 0,
    refetchOnMount: 'always',
  })
  const equipment = equipmentData?.equipment

  const { data: sessionTypes = [] } = useSiteSessionTypes(siteId)
  const sessionTypeOptions = useMemo(
    () => sessionTypes.map((st) => ({ label: st.name, value: st.siteSessionTypeId })),
    [sessionTypes]
  )

  // Status section — client data
  const assignments = equipment?.assignments || []
  const latestAssignment = assignments[0]
  const previousAssignment = assignments[1]

  const tanLengthMinutes =
    (latestAssignment?.timerMinutes ?? 0) + (latestAssignment?.delayMinutes ?? 0)
  const delayMinutes = latestAssignment?.delayMinutes ?? 0
  let timeRemainingMinutes = null
  if (latestAssignment?.createdAt && tanLengthMinutes != null) {
    const startAt = new Date(latestAssignment.createdAt).getTime() + delayMinutes * 60 * 1000
    const endAt = new Date(startAt + tanLengthMinutes * 60 * 1000)
    const diff = differenceInMinutes(endAt, new Date())
    timeRemainingMinutes = diff > 0 ? diff : 0
  }

  const isActiveStatus = [EQUIPMENT_STATUSES.IN_USE, EQUIPMENT_STATUSES.DELAY].includes(
    equipment?.status
  )
  const currentClientId = isActiveStatus ? latestAssignment?.clientId : null
  const lastClientId = isActiveStatus ? previousAssignment?.clientId : latestAssignment?.clientId
  const assignmentClientIds = [currentClientId, lastClientId].filter(Boolean)
  const { data: assignmentClientsMap } = useClientsByIds(assignmentClientIds, siteId)

  const currentClient = currentClientId ? assignmentClientsMap[currentClientId] : null
  const lastClient = lastClientId ? assignmentClientsMap[lastClientId] : null

  const currentClientName = currentClient
    ? `${currentClient.firstName || ''} ${currentClient.lastName || ''}`.trim()
    : null
  const lastClientName = lastClient
    ? `${lastClient.firstName || ''} ${lastClient.lastName || ''}`.trim()
    : null

  const currentClientUrl = currentClientId
    ? `/s/${siteId}/${ROUTES.clients.children.clientOverview.path.replace(':clientId', currentClientId)}?tab=client-info`
    : undefined
  const lastClientUrl = lastClientId
    ? `/s/${siteId}/${ROUTES.clients.children.clientOverview.path.replace(':clientId', lastClientId)}?tab=client-info`
    : undefined

  // Form
  const formMethods = useForm({
    resolver: zodResolver(VALIDATION_SCHEMA),
    defaultValues: DEFAULT_VALUES,
    mode: 'onTouched',
  })

  useEffect(() => {
    if (equipment && !dialogState?.formValues) {
      formMethods.reset({
        [FIELD_NAMES.EQUIPMENT_NAME]: equipment.name,
        [FIELD_NAMES.EQUIPMENT_DESCRIPTION]: equipment.description,
        [FIELD_NAMES.MANUFACTURER]: equipment.manufacturer,
        [FIELD_NAMES.TIMER_ID]: equipment.timerId.toString(),
        [FIELD_NAMES.QUALITY_CATEGORY]: equipment.qualityCategory,
        [FIELD_NAMES.IS_UV_EQUIPMENT]: equipment.category === EQUIPMENT_CATEGORIES.UV,
        [FIELD_NAMES.NON_UV_CATEGORY]: equipment.nonUvCategory || '',
        [FIELD_NAMES.DEFAULT_TIMER_TIME]: equipment.defaultTimerMinutes,
        [FIELD_NAMES.MINIMUM_TIMER_TIME]: equipment.minTimerMinutes,
        [FIELD_NAMES.MAXIMUM_TIMER_TIME]: equipment.maxTimerMinutes,
        [FIELD_NAMES.DEFAULT_DELAY_TIME]: equipment.defaultDelayMinutes,
        [FIELD_NAMES.MINIMUM_DELAY_TIME]: equipment.minDelayMinutes,
        [FIELD_NAMES.MAXIMUM_DELAY_TIME]: equipment.maxDelayMinutes,
        [FIELD_NAMES.SITE_SESSION_TYPE_IDS]: equipment.siteSessionTypeIds || [],
      })
    } else if (dialogState?.formValues) {
      Object.keys(dialogState.formValues).forEach((key) => {
        if (key === FIELD_NAMES.IMAGE) return
        formMethods.setValue(key, dialogState.formValues[key], {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
      })
    }
  }, [equipment, formMethods, dialogState?.formValues])

  const { handleSubmit, watch } = formMethods
  const isUvEquipmentValue = watch(FIELD_NAMES.IS_UV_EQUIPMENT)

  const onSubmit = (data) => {
    updateEquipment({
      ...data,
      category: data.isUvEquipment ? EQUIPMENT_CATEGORIES.UV : EQUIPMENT_CATEGORIES.REGULAR,
      timerId: parseInt(data.timerId),
      nonUvCategory: data.isUvEquipment ? null : data.nonUvCategory,
      siteSessionTypeIds: (data.siteSessionTypeIds || []).filter((id) => id !== ''),
      siteId,
      equipmentId,
    })
  }

  const handleDelete = () => {
    deleteEquipment({ equipmentId })
  }

  const updateExistingEquipmentImage = useEquipmentImageUpload(equipmentData, {
    onSuccess: async (data) => {
      setDialogState({
        image: imageToUrl(data.image),
        imageExtension: data.fileExtension,
        croppedImage: data.croppedImage,
      })
      filterDialogStack(dialogFilterParams.key, dialogFilterParams.value)
    },
  })

  const openPhotoDialog = () => {
    pushDialog(
      ModalUploadPhoto,
      { hasCam: false, mutation: updateExistingEquipmentImage },
      {
        formValues: formMethods.getValues(),
        skipSuccessDialog: true,
        dialogFilterParams,
      }
    )
  }

  const handleViewActivityLog = () => {
    if (!equipment) return
    pushDialog(EquipmentTestLogDialog, { equipmentId, equipmentName: equipment.name })
  }

  const imageUrl =
    dialogState?.image ||
    equipmentData?.equipment?.image?.medium ||
    equipmentData?.equipment?.image?.db

  if (isLoading) {
    return (
      <DialogLayout
        closeType={CLOSE_TYPES.ICON}
        closeAction={() => {}}
        title={t('checkIn:equipments.addEquipment.editTitle')}
        size={SIZES.SM}
        primaryAction={{
          label: t('checkIn:equipments.addEquipment.saveChanges'),
          props: { type: 'submit', isLoading: true },
        }}
        secondaryAction={{
          label: t('checkIn:equipments.addEquipment.delete'),
          icon: TrashSmallIcon,
          props: { type: 'button', disabled: true },
        }}
      >
        <DialogContent sx={styles.dialogContent}>
          <Loading />
        </DialogContent>
      </DialogLayout>
    )
  }

  return (
    <FormProvider {...formMethods}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={styles.form}>
        <DialogLayout
          closeType={CLOSE_TYPES.ICON}
          title={t('checkIn:equipments.addEquipment.editTitle')}
          size={SIZES.SM}
          primaryAction={{
            label: t('checkIn:equipments.addEquipment.saveChanges'),
            props: { type: 'submit', isLoading: isUpdating },
          }}
          secondaryAction={{
            label: t('checkIn:equipments.addEquipment.delete'),
            action: handleDelete,
            icon: TrashSmallIcon,
            props: { type: 'button', disabled: isUpdating || isDeleting, isLoading: isDeleting },
          }}
        >
          <DialogContent sx={styles.dialogContent}>
            {/* Status section */}
            <ModalGroup
              title={t('checkIn:equipments.detail.status')}
              titleStyles={styles.modalGroupTitleStyles}
              actionConfig={{
                label: t('checkIn:equipments.detail.viewTestLog'),
                icon: PlaySmallIcon,
                action: handleViewActivityLog,
              }}
            >
              <Box sx={styles.statusContainer}>
                {equipment && (
                  <InfoPanel
                    type="time"
                    dataList={[
                      {
                        label: t('checkIn:equipments.detail.status'),
                        description: t(
                          `checkIn:equipments.assignToEquipment.${STATUS_I18N_KEY_MAP[equipment.status]}`
                        ),
                      },
                      ...(isActiveStatus
                        ? [
                            {
                              label: t('checkIn:equipments.detail.tanLength'),
                              description: t('checkIn:equipments.detail.minutesValue', {
                                count: tanLengthMinutes ?? 0,
                              }),
                            },
                            {
                              label: t('checkIn:equipments.detail.timeRemaining'),
                              description:
                                typeof timeRemainingMinutes === 'number'
                                  ? t('checkIn:equipments.detail.minutesValue', {
                                      count: timeRemainingMinutes,
                                    })
                                  : '—',
                            },
                          ]
                        : []),
                    ]}
                  />
                )}
                {isActiveStatus && currentClientName && (
                  <InfoPanel
                    type="time"
                    dataList={[
                      {
                        label: t('checkIn:equipments.detail.clientInEquipment'),
                        description: currentClientName,
                      },
                    ]}
                    action={{
                      label: t('checkIn:equipments.detail.viewClientRecord'),
                      href: currentClientUrl,
                    }}
                  />
                )}
                {lastClientName && (
                  <InfoPanel
                    type="info"
                    dataList={[
                      {
                        label: t('checkIn:equipments.detail.lastClientInEquipment'),
                        description: lastClientName,
                      },
                    ]}
                    action={{
                      label: t('checkIn:equipments.detail.viewClientRecord'),
                      href: lastClientUrl,
                    }}
                  />
                )}
              </Box>
            </ModalGroup>

            {/* Equipment information section */}
            <ModalGroup
              title={t('checkIn:equipments.detail.equipmentInformation')}
              titleStyles={styles.modalGroupTitleStyles}
            >
              <Box sx={styles.formFields}>
                <TextField
                  name={FIELD_NAMES.EQUIPMENT_NAME}
                  label={t('checkIn:equipments.addEquipment.equipmentName')}
                  required
                />

                <TextArea
                  name={FIELD_NAMES.EQUIPMENT_DESCRIPTION}
                  label={t('checkIn:equipments.addEquipment.equipmentDescription')}
                  required
                  rows={4}
                />

                <Box sx={styles.fieldRow}>
                  <TextField
                    name={FIELD_NAMES.MANUFACTURER}
                    label={t('checkIn:equipments.addEquipment.manufacturer')}
                  />
                  <TextField
                    name={FIELD_NAMES.TIMER_ID}
                    label={t('checkIn:equipments.addEquipment.timerId')}
                    required
                    helperText={t('checkIn:equipments.manageEquipments.timerIdHelperText')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                <Box sx={styles.imageSection}>
                  <Typography typography="header4" color="gray.80">
                    {t('checkIn:equipments.addEquipment.equipmentImage')}
                  </Typography>
                  <Box sx={styles.imageContainer}>
                    <Avatar
                      variant={AVATAR_VARIANT.CIRCULAR}
                      avatarType={AVATAR_TYPES.SINGLE_AVATAR}
                      alt={t('userProfile.profileAltText')}
                      size={AVATAR_SIZE.EXTRA_LARGE}
                      item={{ src: imageUrl }}
                      onClick={openPhotoDialog}
                      avatar={styles.avatar}
                    />
                    {imageUrl && (
                      <Box sx={styles.imageInfo}>
                        <Typography typography="small" color="gray.64">
                          {getFormattedImageName(imageUrl, dialogState?.imageExtension)}
                        </Typography>
                        <Button
                          variant={BUTTON_VARIANTS.TEXT}
                          startIcon={PenSmallIcon}
                          label={t('checkIn:equipments.addEquipment.updateImage')}
                          theme={BUTTON_THEME.GRAY}
                          type="button"
                          sx={styles.updateImageButton}
                          onClick={openPhotoDialog}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box sx={styles.categorySection}>
                  <RadioGroup
                    id="equipment-category"
                    name={FIELD_NAMES.QUALITY_CATEGORY}
                    options={EQUIPMENT_QUALITY_CATEGORIES_OPTIONS.map((opt) => ({
                      ...opt,
                      label: t(opt.label),
                    }))}
                    radioType={RADIO_TYPES.BOX}
                    row
                    radioGroupStyles={{ gap: 2, ...styles.categoryRadioGroup }}
                    label={t('checkIn:equipments.addEquipment.category')}
                    labelStyles={{ typography: 'header4' }}
                  />
                </Box>

                <Switch
                  name={FIELD_NAMES.IS_UV_EQUIPMENT}
                  type={SWITCH_TYPES.LABEL}
                  label={t('checkIn:equipments.addEquipment.uvEquipment')}
                />

                {!isUvEquipmentValue && (
                  <Dropdown
                    name={FIELD_NAMES.NON_UV_CATEGORY}
                    label={t('checkIn:equipments.addEquipment.nonUvCategory')}
                    options={NON_UV_CATEGORY_OPTIONS}
                    required
                  />
                )}

                <Dropdown
                  name={FIELD_NAMES.SITE_SESSION_TYPE_IDS}
                  label={t('checkIn:equipments.addEquipment.sessionTypes')}
                  options={sessionTypeOptions}
                  multiple
                  shouldSearch
                  required
                />

                {isUvEquipmentValue && (
                  <Box sx={styles.timerFields}>
                    <TextField
                      name={FIELD_NAMES.DEFAULT_TIMER_TIME}
                      inputProps={{ type: 'number' }}
                      label={t('checkIn:equipments.addEquipment.defaultTimerTime')}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography typography="large" color="gray.56">
                              {t('checkIn:equipments.addEquipment.minutes')}
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Box sx={styles.fieldRow}>
                      <TextField
                        name={FIELD_NAMES.MINIMUM_TIMER_TIME}
                        inputProps={{ type: 'number' }}
                        label={t('checkIn:equipments.addEquipment.minimumTimerTime')}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography typography="large" color="gray.56">
                                {t('checkIn:equipments.addEquipment.minutes')}
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        name={FIELD_NAMES.MAXIMUM_TIMER_TIME}
                        inputProps={{ type: 'number' }}
                        label={t('checkIn:equipments.addEquipment.maximumTimerTime')}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography typography="large" color="gray.56">
                                {t('checkIn:equipments.addEquipment.minutes')}
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                    <TextField
                      name={FIELD_NAMES.DEFAULT_DELAY_TIME}
                      inputProps={{ type: 'number' }}
                      label={t('checkIn:equipments.addEquipment.defaultDelayTime')}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography typography="large" color="gray.56">
                              {t('checkIn:equipments.addEquipment.minutes')}
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Box sx={styles.fieldRow}>
                      <TextField
                        name={FIELD_NAMES.MINIMUM_DELAY_TIME}
                        inputProps={{ type: 'number' }}
                        label={t('checkIn:equipments.addEquipment.minimumDelayTime')}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography typography="large" color="gray.56">
                                {t('checkIn:equipments.addEquipment.minutes')}
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        name={FIELD_NAMES.MAXIMUM_DELAY_TIME}
                        inputProps={{ type: 'number' }}
                        label={t('checkIn:equipments.addEquipment.maximumDelayTime')}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography typography="large" color="gray.56">
                                {t('checkIn:equipments.addEquipment.minutes')}
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </ModalGroup>
          </DialogContent>
        </DialogLayout>
      </Box>
    </FormProvider>
  )
}

EquipmentEditDialog.propTypes = {
  equipmentId: PropTypes.string.isRequired,
}
